/* eslint-disable no-underscore-dangle */
/* eslint-disable no-case-declarations */
import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import {v4} from "uuid";
import {withResizeDetector} from "react-resize-detector";
/* Components */
import ModalDefault from "../../../common/modal/modal-default";
import Loader from "../../../common/loader/Loader";
import GridForm from "./grid-form";
import GridContent from "./grid-content";
/* Services */
import DashboardService, {CustomFieldEntity} from "../../../../services/dashboard-service";
/* Helpers */
import {getSolutionLabel} from "../../../../helpers/chart-helpers";
import {steveDataStorage} from "../../../../helpers/sdk-helpers";
import {isUserWithDashboardViewerRole} from "../../../../helpers/dashboard-helpers";
import {
    getEnabledCustomColumns,
    getSpreadedColumnConfig,
    mergeSavedConfigToDefaultConfig,
    getFilterQueryForDevices,
} from "./grid-helper";
import {goToSolutions, shutdownFeed} from "../widget-helpers";
import {DEVICE_CUSTOM_FIELDS} from "../../../../pagingfeed/column-helpers/deviceConstants";
/* Util */
import {
    auditPoliciesColumnConfig,
    deviceManagerDevicesColumnConfig,
    fileServersColumnConfig,
    filesColumnConfig,
    osPlansColumnConfig,
    schedulesColumnConfig,
} from "../../../../pagingfeed/index";
import {DATA_TYPE_NAMES} from "../widget-constants";
/* Styles */
import "./grid.scss";
import WidgetHeader from "../widget-header/WidgetHeader";
import WidgetHeaderButton from "../widget-header/widget-header-button/WidgetHeaderButton";

const INITIAL_VALUES = {
    filter: {
        humanQuery: {},
        mongoQuery: {},
    },
    columnsConfig: {},
    data: {},
    dataType: null,
    dataFeed: null,
};

const DEFAULT_SORT = {name: 1};

class GridWidget extends React.PureComponent {
    constructor(props) {
        super(props);
        this.customFieldColumns = {};

        this.deviceCustomFields = DEVICE_CUSTOM_FIELDS;
        this._CustomFieldsEntityDef = CustomFieldEntity;
        this.dataFeed = INITIAL_VALUES.dataFeed;
        this.state = {
            formData: {
                title: this.props.data.title,
                dataType: this.props.data.config.dataType || INITIAL_VALUES.dataType,
                filter: this.props.data.config.filter || INITIAL_VALUES.filter,
            },
            tempConfigData: {
                title: this.props.data.title,
                dataType: this.props.data.config.dataType || INITIAL_VALUES.dataType,
                filter: this.props.data.config.filter || INITIAL_VALUES.filter,
                columnsConfig: INITIAL_VALUES.columnsConfig,
            },
            columnsConfig: INITIAL_VALUES.columnsConfig,
            isSettingsModalShown: false,
            widgetId: this.props.data.id || v4(),
            isLoadingData: false,
            isLoadingColumnConfig: false,
            errorMessage: "",
            dataInitialized: false,
        };
    }

    async componentDidMount() {
        const dataType = this.state.formData.dataType;
        const feedFilter = this.state.formData.filter;

        this.customFieldColumns = await DashboardService.getCustomFields();

        const columnsConfig = await this.getInitialColumnsConfig();
        if (dataType) {
            this.initFeed(dataType, columnsConfig, feedFilter);
        }
    }

    componentWillUnmount() {
        this.customFieldColumns = undefined;
        if (this.dataFeed) {
            shutdownFeed(this.dataFeed);
            this.dataFeed = null;
        }
    }

    getInitialColumnsConfig = async () => {
        const columnsConfig = this.props.data.config.columnsConfig
            ? await mergeSavedConfigToDefaultConfig(
                  this.props.data.config.dataType,
                  this.props.data.config.columnsConfig,
                  this._CustomFieldsEntityDef,
                  this.customFieldColumns,
                  this.props.osMap,
                  this.props.fileServersMap,
                  this.props.osPaths,
                  this.props.data,
                  this.deviceCustomFields
              )
            : INITIAL_VALUES.columnsConfig;

        return columnsConfig;
    };

    handleDataTypeChange = (field, props) => async (value) => {
        props.setFieldValue(field, value);
        this.setState({isLoadingColumnConfig: true});
        await this.changeTempColumnsConfig(value, props);
        this.setState({isLoadingColumnConfig: false});
    };

    handleFormFieldChange = (field, props) => (value) => {
        this.setState({
            tempConfigData: {
                ...this.state.tempConfigData,
                [field]: value,
            },
        });
        props.setFieldValue(field, value);
    };

    saveColumnConfig = (columnsConfig) => {
        this.setState({
            tempConfigData: {
                ...this.state.tempConfigData,
                columnsConfig,
            },
        });
    };

    feedFactory = async (dataType, columnsConfig, filter) => {
        let dataFeed = INITIAL_VALUES.dataFeed; // check if this can be set as null
        let errorMessage = "";

        const stringifiedColumnSorting = steveDataStorage(
            "entity_grid_gl",
            `${this.state.widgetId}_userData`,
            false
        ).get("columnSorting");

        const userSort = stringifiedColumnSorting
            ? JSON.parse(stringifiedColumnSorting)
            : DEFAULT_SORT;

        try {
            switch (dataType) {
                case DATA_TYPE_NAMES.AUDIT_POLICIES:
                    dataFeed = await DashboardService.getAuditPolicies(
                        columnsConfig,
                        filter,
                        userSort
                    );
                    break;
                case DATA_TYPE_NAMES.DEVICES:
                    const newColumnsConfig = _.assign(
                        {},
                        columnsConfig,
                        ...getEnabledCustomColumns(
                            {},
                            {},
                            [],
                            columnsConfig,
                            this.props.data.config.columnsConfig
                        )
                    );

                    const filterQuery = getFilterQueryForDevices(dataType, filter);
                    dataFeed = await DashboardService.getDevices(
                        newColumnsConfig,
                        filterQuery,
                        userSort
                    );
                    break;
                case DATA_TYPE_NAMES.FILE_SERVERS:
                    dataFeed = await DashboardService.getFileServers(
                        columnsConfig,
                        filter,
                        userSort
                    );
                    break;
                case DATA_TYPE_NAMES.FILES:
                    dataFeed = await DashboardService.getFiles(columnsConfig, filter, userSort);
                    break;
                case DATA_TYPE_NAMES.OS_PLANS:
                    dataFeed = await DashboardService.getOsPlans(columnsConfig, filter, userSort);
                    break;
                case DATA_TYPE_NAMES.SCHEDULES:
                    dataFeed = await DashboardService.getSchedules(columnsConfig, filter, userSort);
                    break;
                default:
                    dataFeed = INITIAL_VALUES.dataFeed;
            }
        } catch (err) {
            errorMessage = err.errorMsg;
        }

        return {
            dataFeed,
            errorMessage,
        };
    };

    initFeed = async (dataType, columnsConfig, filter) => {
        this.setState({
            isLoadingData: true,
            dataInitialized: false,
        });
        const {dataFeed, errorMessage} = await this.feedFactory(
            dataType.value,
            columnsConfig,
            filter
        );
        this.dataFeed = dataFeed;
        this.setState({
            columnsConfig,
            tempConfigData: {
                ...this.state.tempConfigData,
                columnsConfig,
            },
            isLoadingData: false,
            errorMessage,
            dataInitialized: true,
        });
    };

    toggleSettingsModal = (e) => {
        e.stopPropagation();
        this.setState({
            tempConfigData: {
                ...this.state.tempConfigData,
                filter: {...this.state.formData.filter},
                dataType: {...this.state.formData.dataType},
                columnsConfig: {...this.state.columnsConfig},
            },
            isSettingsModalShown: !this.state.isSettingsModalShown,
        });
    };

    handleSave = async (values) => {
        const dataType = values.dataType;
        const stateDataType = this.state.formData.dataType;
        const filter = values.filter;
        const stateFilter = this.state.formData.filter.mongoQuery;
        let errorMessage = "";

        this.setState({
            isSettingsModalShown: false,
            isLoadingData: true,
        });

        if (
            (stateDataType && stateDataType.value !== dataType.value) ||
            !_.isEqual(this.state.columnsConfig, this.state.tempConfigData.columnsConfig) ||
            !_.isEqual(stateFilter, filter.mongoQuery)
        ) {
            if (this.dataFeed) {
                await shutdownFeed(this.dataFeed);
                this.dataFeed = null;
            }
            const columnsConfig = this.state.tempConfigData.columnsConfig;
            const factoryData = await this.feedFactory(dataType.value, columnsConfig, filter);
            this.dataFeed = factoryData.dataFeed;
            errorMessage = factoryData.errorMessage;
        }

        this.setState(
            {
                formData: values,
                columnsConfig: {...this.state.tempConfigData.columnsConfig},
                errorMessage,
                isLoadingData: false,
                dataInitialized: true,
            },
            () => {
                this.props.onUpdate({
                    ...this.props.data,
                    title: this.state.formData.title,
                    config: {
                        ...this.props.data.config,
                        dataType: this.state.formData.dataType,
                        filter: this.state.formData.filter,
                        columnsConfig: this.reduceColumnConfig(this.state.columnsConfig),
                        widgetId: this.state.widgetId,
                    },
                });
            }
        );
    };

    reduceColumnConfig(columnConfig) {
        return Object.entries(columnConfig).reduce(
            (reducedColumnsConfig, [columnKey, {shown, priority}]) => ({
                ...reducedColumnsConfig,
                [columnKey]: {shown, priority},
            }),
            {}
        );
    }

    changeTempColumnsConfig = async (dataType, props) => {
        let tempColumnsConfig;

        const commonProps = {
            dataType,
            customFieldsEntityDef: this._CustomFieldsEntityDef,
            customFieldColumns: this.customFieldColumns,
            osMap: this.props.osMap,
            fileServersMap: this.props.fileServersMap,
            osPaths: this.props.osPaths,
            data: this.props.data,
            deviceCustomFields: this.deviceCustomFields,
        };

        switch (dataType.value) {
            case DATA_TYPE_NAMES.AUDIT_POLICIES:
                tempColumnsConfig = auditPoliciesColumnConfig;
                break;
            case DATA_TYPE_NAMES.DEVICES:
                const deviceColumnConfig = await deviceManagerDevicesColumnConfig.getColumnConfig();
                tempColumnsConfig = getSpreadedColumnConfig(deviceColumnConfig, commonProps);
                break;
            case DATA_TYPE_NAMES.FILE_SERVERS:
                tempColumnsConfig = fileServersColumnConfig;
                break;
            case DATA_TYPE_NAMES.FILES:
                tempColumnsConfig = getSpreadedColumnConfig(filesColumnConfig, commonProps);
                break;
            case DATA_TYPE_NAMES.OS_PLANS:
                tempColumnsConfig = osPlansColumnConfig;
                break;
            case DATA_TYPE_NAMES.SCHEDULES:
                tempColumnsConfig = schedulesColumnConfig;
                break;
            default:
                tempColumnsConfig = INITIAL_VALUES.columnsConfig;
        }

        props.setFieldValue("filter", INITIAL_VALUES.filter);

        this.setState({
            tempConfigData: {
                ...this.state.tempConfigData,
                dataType,
                columnsConfig: _.cloneDeep(tempColumnsConfig),
                filter: INITIAL_VALUES.filter,
            },
        });
    };

    handleSolutionRedirect = (event) => {
        let sln;
        let obj = {};
        const openSolutionInNewTab = this.props.openSolutionsInNewTab;

        if (event && event.selectedRow) {
            const selection = [event.selectedRow];
            obj = {
                ...obj,
                selection,
                openEditor: true,
            };
        }

        if (Object.keys(this.state.formData.filter).length > 0) {
            obj = {
                ...obj,
                filter: this.state.formData.filter.mongoQuery,
            };
        }
        if ([DATA_TYPE_NAMES.DEVICES].includes(this.state.formData.dataType.value)) {
            obj = {
                ...obj,
                groupingField: {},
            };
        }
        goToSolutions(
            obj,
            this.state.formData.dataType.value,
            this.props.isMobileDevice,
            sln,
            openSolutionInNewTab
        );
    };

    render() {
        return (
            <>
                <div className="glu-dashboard-card glu-content glu-grid-widget">
                    {this.state.isLoadingData ||
                    (!this.state.dataInitialized &&
                        this.state.formData.dataType &&
                        this.state.formData.dataType.value) ? (
                        <Loader />
                    ) : null}
                    <WidgetHeader
                        isEditAllowed={this.props.isEditAllowed}
                        title={this.state.formData.title}
                        height={this.props.height}
                        width={this.props.width}
                        showWidgetInfo={this.props.showWidgetInfo(this.props.data)}
                        openConfiguration={this.toggleSettingsModal}
                        onRemove={this.props.onRemove(this.props.data)}
                        onClone={this.props.onClone(this.props.data)}
                    >
                        <WidgetHeaderButton
                            showButton={
                                !this.props.isEditAllowed &&
                                !this.props.isMobileDevice &&
                                !this.state.errorMessage &&
                                !isUserWithDashboardViewerRole()
                            }
                            iconName="exit-from-the-app"
                            onClick={this.handleSolutionRedirect}
                            hoverTooltip={`Open in ${getSolutionLabel(
                                this.state.formData.dataType?.value || ""
                            )}`}
                        />
                    </WidgetHeader>
                    <div className="glu-content__body">
                        <GridContent
                            errorMessage={this.state.errorMessage}
                            dataInitialized={this.state.dataInitialized}
                            dataType={this.state.dataType}
                            dataFeed={this.dataFeed}
                            isEditAllowed={this.props.isEditAllowed}
                            toggleSettingsModal={this.toggleSettingsModal}
                            widgetId={this.state.widgetId}
                            columnsConfig={this.state.columnsConfig}
                            formData={this.state.formData}
                            handleSolutionRedirect={this.handleSolutionRedirect}
                            isMobileDevice={this.props.isMobileDevice}
                        />
                    </div>
                </div>
                {this.state.isSettingsModalShown && (
                    <ModalDefault isOpen={this.state.isSettingsModalShown}>
                        <div className="glu-modal-content">
                            <div className="glu-modal__header">
                                <div className="glu-modal__title glu-flex-grow">
                                    {this.state.formData.title}
                                </div>
                                <div className="glu-flex-shrink">
                                    <button
                                        aria-label="close"
                                        type="button"
                                        className="glu-btn-link g-icon g-icon-close"
                                        onClick={this.toggleSettingsModal}
                                    />
                                </div>
                            </div>
                            <GridForm
                                initialValues={this.state.formData}
                                onFormSubmit={this.handleSave}
                                onCancel={this.toggleSettingsModal}
                                handleFormFieldChange={this.handleFormFieldChange}
                                handleDataTypeChange={this.handleDataTypeChange}
                                saveColumnConfig={this.saveColumnConfig}
                                tempConfigData={this.state.tempConfigData}
                                isLoadingColumnConfig={this.state.isLoadingColumnConfig}
                            />
                        </div>
                    </ModalDefault>
                )}
            </>
        );
    }
}

GridWidget.propTypes = {
    data: PropTypes.objectOf(PropTypes.any).isRequired,
    isEditAllowed: PropTypes.bool,
    onRemove: PropTypes.func,
    onUpdate: PropTypes.func,
    showWidgetInfo: PropTypes.func,
    onClone: PropTypes.func,
    osMap: PropTypes.arrayOf(PropTypes.any),
    osPaths: PropTypes.objectOf(PropTypes.any),
    fileServersMap: PropTypes.arrayOf(PropTypes.any),
    selectedOrganization: PropTypes.objectOf(PropTypes.any),
    isMobileDevice: PropTypes.bool,
};

GridWidget.defaultProps = {
    isEditAllowed: false,
    onRemove: undefined,
    onUpdate: undefined,
    showWidgetInfo: undefined,
    onClone: undefined,
    osMap: [],
    osPaths: {},
    fileServersMap: [],
    selectedOrganization: null,
    isMobileDevice: false,
};

export default withResizeDetector(GridWidget);
